import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import { PreviousLives as PreviousLivesComponent } from "@domestika-dev/prudencio/dist/molecules/DomestikaLiveCarousels";
import { Flex } from "@domestika-dev/prudencio/dist/atoms/Layout";
import { Text } from "@domestika-dev/prudencio/dist/atoms/Typography";
import * as DomestikaLiveTypes from "../../constants/domestikaLiveTypes";
import * as LiveSessionStates from "../../constants/liveSessionStates";
import { useLanguagesList } from "../../customHooks/useLanguagesList";
import { getAssetPath } from "../../utils/assetsUtils";
import { defaultStreamer, mapTeacher, isTeacher, liveRedirect } from "./utils";
import { formatLiveSessionDate } from "../../utils/dateUtils";
import {
  injectSmoothScrollPolyfill,
  getClientWindowSize,
  WindowSizes,
} from "../../utils/windowUtils";
import When from "../../../../common/When";
import { DomestikaContext } from "../../../../DomestikaContext";
import { LiveSessionContext } from "../../context/LiveSessionContext";

const IMG_BREAKPOINTS = {
  [WindowSizes.XS]: 288,
  [WindowSizes.SM]: 350,
  [WindowSizes.MD]: 296,
  [WindowSizes.LG]: 380,
};

export const PreviousLives = ({ category }) => {
  const intl = useIntl();
  const domain = useContext(DomestikaContext);
  const { user } = useContext(LiveSessionContext);

  const { getLanguagesList } = useLanguagesList();
  const languagesList = useRef(getLanguagesList());
  const previousLivesRef = useRef(null);

  const [carouselLoading, setCarouselLoading] = useState(true);
  const [carouselItems, setCarouselItems] = useState([]);
  const liveLabelRef = useRef(intl.formatMessage({ id: "live_sessions.carousel_card.now_live" }));
  const windowWidthRef = useRef(window.innerWidth);

  const getLanguageLabel = useCallback(
    (language) => languagesList.current?.find(({ value }) => value === language)?.label,
    [languagesList]
  );

  const mapItems = async (liveStreams) => {
    await injectSmoothScrollPolyfill();

    const mappedItems = liveStreams.map(
      ({
        id,
        title,
        scheduleDate,
        liveType,
        language,
        canAccess,
        coverImage,
        teachers,
        course,
      }) => {
        const streamers = teachers?.length ? teachers.map(mapTeacher) : [defaultStreamer];

        let locked = false;
        let lockedTooltip = null;
        let student = false;
        let studentTooltip = null;

        if (liveType === DomestikaLiveTypes.DOMESTIKA_LIVE) {
          locked = !canAccess;
          if (locked) {
            lockedTooltip = intl.formatMessage(
              { id: "live_sessions.carousel_card.locked_tooltip" },
              { course: course.title }
            );
          }

          student = canAccess && !isTeacher(course, user);
          if (student) {
            studentTooltip = intl.formatMessage(
              { id: "live_sessions.carousel_card.unlocked_tooltip" },
              { course: course.title }
            );
          }
        }

        return {
          liveLabel: liveLabelRef.current,
          locked,
          lockedTooltip,
          student,
          studentTooltip,
          liveInfo: {
            scheduledAt: formatLiveSessionDate(scheduleDate),
            language: getLanguageLabel(language),
            title,
            coverUrl: getAssetPath(
              coverImage,
              IMG_BREAKPOINTS[getClientWindowSize(windowWidthRef.current)]
            ),
          },
          streamers,
          onClick: () => {
            liveRedirect[liveType](id, course?.id, LiveSessionStates.FINISHED);
          },
        };
      }
    );

    setCarouselItems(mappedItems);
    setCarouselLoading(false);
  };

  const loadPreviousLives = async () => {
    const [error, data] = await domain.GetListLiveStreamUseCase.execute({
      filter: {
        state: LiveSessionStates.FINISHED,
        hasRecording: true,
        category: category.id,
      },
      sorts: [
        { field: "startedAt", order: domain.config("SORT_ORDER").DESC },
        { field: "hasAccess", order: domain.config("SORT_ORDER").ASC },
        { field: "type", order: domain.config("SORT_ORDER").ASC },
      ],
    });

    if (error || !data?.liveStreams?.length) {
      setCarouselLoading(false);
      return;
    }

    mapItems(data.liveStreams);
  };

  const getPreviousLivesRef = (node) => {
    if (node !== null && !previousLivesRef.current) {
      previousLivesRef.current = new IntersectionObserver(
        ([entry]) => {
          if (entry?.isIntersecting) {
            previousLivesRef.current.disconnect();
            loadPreviousLives();
          }
        },
        {
          rootMargin: "200px",
        }
      );
      previousLivesRef.current.observe(node);
    }
  };

  useEffect(() => {
    return () => {
      if (previousLivesRef.current) {
        previousLivesRef.current.disconnect();
      }
    };
  }, []);

  return (
    <When condition={carouselLoading || !!carouselItems.length}>
      {() => (
        <Flex
          flexDirection="column"
          alignItems="center"
          marginTop={[8, 8, "80px"]}
          ref={getPreviousLivesRef}
        >
          <Flex width={["100%", "100%", "928px", "1200px"]} paddingLeft={[4, 5, 0, "10px"]}>
            <Text size="large" fontWeight="medium" as="h3">
              {category.name}
            </Text>
          </Flex>
          <Flex width="100%" marginTop={5} overflow="hidden">
            <PreviousLivesComponent items={carouselItems} loading={carouselLoading} />
          </Flex>
        </Flex>
      )}
    </When>
  );
};

PreviousLives.propTypes = {
  category: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
  }).isRequired,
};

export default PreviousLives;
